/**
 * TOUR-BOOK RAIL (cockpit bundle): RUN OF SHOW (future dates, numbered) ·
 * Next line · + nouvelle date (REAL create_gig via the fiche) · À TRAITER
 * (the REAL dead-letter queue + engine parks — dismiss is the real
 * /api/attention dismiss; tapping an entry pre-fills the console) · MISSIONS
 * (contract seam, honest empty state until the P5 agent) · JOURNAL (this
 * session's actions) · ARCHIVE (past dates, collapsed).
 *
 * On mobile the rail IS the list view (master → detail).
 */
import { lazy, Suspense, useState } from "react";
import { t, type Locale } from "../i18n.ts";
import type { GigRecord } from "../db/idb.ts";
import type { Editor } from "../outbox/mutations.ts";
import { compareGigs, gigDate, gigDisplayName } from "../views/GigList.tsx";

// Rarely-opened, destructive surface → lazy chunk, off the core-shell weight budget.
const GigTrash = lazy(() => import("./GigTrash.tsx").then((m) => ({ default: m.GigTrash })));
import type { AttentionItem } from "../views/attentionApi.ts";

export interface MissionEntry {
  id: string;
  label: string;
  status: "run" | "done" | "failed";
  steps: readonly string[];
  cards: readonly import("../omnibar/brain.ts").ApplyCard[];
  summary: string;
}

export interface JournalEntry {
  time: string; // HH:MM
  text: string;
  tone: "ok" | "warn" | "muted" | "accent";
}

export interface RailProps {
  locale: Locale;
  gigs: readonly GigRecord[];
  all: readonly GigRecord[]; // every gig incl. tombstoned; the Trash is derived here
  editor?: Editor;
  openGigId: string | null;
  canWrite: boolean;
  nowMs: number;
  attention: readonly AttentionItem[];
  journal: readonly JournalEntry[];
  onOpen(gigId: string): void;
  onNewGig(): void;
  onDismissAttention(id: string): void;
  onPrefillConsole(q: string): void;
  missions?: readonly MissionEntry[];
  onLoadMissionCards?(m: MissionEntry): void;
}

function sv(gig: GigRecord, key: string): string {
  const v = gig.state.gig?.fields[key];
  return typeof v === "string" ? v : "";
}

/** «ven 04.07 · Lyon · Nuits Sonores» — the wake-up line: weekday first
 * (Eduardo: "this is the first thing crew sees — the ahh this is today"). */
function railLine(gig: GigRecord, locale: Locale): string {
  const date = gigDate(gig);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (m === null) {
    const city = sv(gig, "city");
    return ["—", city, gigDisplayName(gig)].filter((x) => x !== "").join(" · ");
  }
  const wd = new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : "en-GB", { weekday: "short", timeZone: "UTC" })
    .format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))))
    .replace(".", "");
  const dd = `${m[3]}.${m[2]}`;
  const city = sv(gig, "city");
  const name = gigDisplayName(gig);
  return [`${wd} ${dd}`, city, name].filter((x) => x !== "").join(" · ");
}

/** Short relative marker: AUJ (gold) · J+1 · J+2 … — today/tomorrow at a
 * glance, quiet after a week. */
function relMarker(gig: GigRecord, todayDay: string, locale: Locale): { label: string; today: boolean } | null {
  const d = gigDate(gig).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const days = Math.round((Date.parse(d) - Date.parse(todayDay)) / 86_400_000);
  if (days === 0) return { label: locale === "fr" ? "AUJ" : "TDY", today: true };
  if (days > 0 && days <= 13) return { label: locale === "fr" ? `J+${days}` : `D+${days}`, today: false };
  return null;
}

function statusChip(gig: GigRecord): string {
  const st = sv(gig, "status").toUpperCase();
  if (st.startsWith("CONF")) return "CONF";
  if (st.startsWith("HOLD") || st.startsWith("PEND")) return "HOLD";
  return st === "" ? "" : st.slice(0, 4);
}

export function Rail(p: RailProps) {
  const { locale } = p;
  const [archiveOpen, setArchiveOpen] = useState(false);

  const today = new Date(p.nowMs);
  const pad = (n: number) => String(n).padStart(2, "0");
  const todayDay = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const sorted = [...p.gigs].sort(compareGigs);
  // A CANCELLED gig is never lost — it leaves the active run-of-show and lives
  // in the ARCHIVE with all its data (Eduardo 2026-07-03: "if a gig got
  // cancelled it's still data for us — keep it in the archive"). Same for a
  // moved date: cancel the old, add the new; the old stays archived.
  const isCancelled = (g: GigRecord) => sv(g, "status").toUpperCase() === "CANCELLED";
  const future = sorted.filter((g) => {
    if (isCancelled(g)) return false;
    const d = gigDate(g).slice(0, 10);
    return d === "" || d >= todayDay; // undated rides with the future book
  });
  const past = sorted
    .filter((g) => isCancelled(g) || (gigDate(g).slice(0, 10) !== "" && gigDate(g).slice(0, 10) < todayDay))
    .reverse();
  // Cheap gate: mount the (lazy) Trash section only if something is trashed —
  // GigTrash owns the header, count and rows, off the core-shell weight budget.
  const hasTrash = p.editor !== undefined && p.canWrite && p.all.some((g) => g.state.gig?.fields["deleted"] === true);

  const next = future.find((g) => gigDate(g).slice(0, 10) > todayDay);
  const nextLine = (() => {
    if (next === undefined) return null;
    const days = Math.round((Date.parse(gigDate(next).slice(0, 10)) - Date.parse(todayDay)) / 86_400_000);
    return t(locale, "rail.next", { city: (sv(next, "city") || gigDisplayName(next)).toUpperCase(), n: days });
  })();

  /** Human line for a parked op — registry labels come later in Attention.tsx;
   * the rail wants ONE readable line, the full surface stays a tap away. */
  const attentionLine = (a: AttentionItem): string => {
    const short = a.reason.length > 64 ? `${a.reason.slice(0, 61)}…` : a.reason;
    return short;
  };

  return (
    <aside className="ck-rail" aria-label={t(locale, "app.gigs")}>
      <div className="ck-rail-title">{t(locale, "rail.run_of_show", { n: future.length })}</div>
      {future.map((g, i) => (
        <button
          key={g.gig_id}
          type="button"
          className={`ck-rail-row${p.openGigId === g.gig_id ? " active" : ""}`}
          onClick={() => p.onOpen(g.gig_id)}
        >
          <span className="ck-rail-n num">{i + 1}.</span>
          <span className="ck-rail-line">{railLine(g, locale)}</span>
          {(() => {
            const rel = relMarker(g, todayDay, locale);
            return rel !== null ? <span className={`ck-rail-rel num${rel.today ? " today" : ""}`}>{rel.label}</span> : null;
          })()}
          <span className="ck-rail-st">{statusChip(g)}</span>
        </button>
      ))}
      {nextLine !== null && <div className="ck-rail-next">{nextLine}</div>}
      {p.canWrite && (
        <button type="button" className="ck-rail-add" onClick={p.onNewGig}>
          {t(locale, "rail.new_date")} <span className="ck-rail-addhint">{t(locale, "rail.new_date_hint")}</span>
        </button>
      )}

      {p.canWrite && (
        <>
          <div className="ck-rail-title">{t(locale, "rail.attention")}</div>
          {p.attention.length === 0 ? (
            <div className="ck-rail-empty">{t(locale, "rail.attention_empty")}</div>
          ) : (
            p.attention.slice(0, 4).map((a) => (
              <div key={a.id} className="ck-rail-alert">
                <span className="ck-rail-mark">!</span>
                <button
                  type="button"
                  className="ck-rail-alert-text"
                  title={t(locale, "rail.attention_prefill")}
                  onClick={() => p.onPrefillConsole(attentionLine(a))}
                >
                  {attentionLine(a)}
                </button>
                <button type="button" className="ck-rail-dismiss" onClick={() => p.onDismissAttention(a.id)}>
                  {t(locale, "rail.attention_dismiss")}
                </button>
              </div>
            ))
          )}

          <div className="ck-rail-title">{t(locale, "rail.missions")}</div>
          {(p.missions ?? []).length === 0 ? (
            <div className="ck-rail-empty">{t(locale, "rail.missions_empty")}</div>
          ) : (
            (p.missions ?? []).map((m) => (
              <div key={m.id} className="ck-rail-mission">
                <div className="ck-rail-mission-head">
                  <span className={`ck-rail-mmark ${m.status}`}>{m.status === "done" ? "✓" : m.status === "failed" ? "×" : "◐"}</span>
                  <span className="ck-rail-mlabel">{m.label}</span>
                  {m.status === "done" && m.cards.length > 0 && p.onLoadMissionCards !== undefined && (
                    <button type="button" className="ck-act ok" onClick={() => p.onLoadMissionCards?.(m)}>
                      {t(locale, "rail.mission_cards", { n: m.cards.length })}
                    </button>
                  )}
                </div>
                {m.status === "run" && <div className="ck-rail-mstep">{t(locale, "mission.working")}</div>}
                {m.status === "failed" && <div className="ck-rail-mstep failed">{t(locale, "mission.failed_line")}</div>}
                {m.status === "done" &&
                  (m.steps.length > 0 ? (
                    m.steps.slice(0, 4).map((st, i) => <div key={i} className="ck-rail-mstep">{st}</div>)
                  ) : (
                    <div className="ck-rail-mstep">{m.summary}</div>
                  ))}
              </div>
            ))
          )}

          <div className="ck-rail-title">{t(locale, "rail.journal")}</div>
          {p.journal.length === 0 ? (
            <div className="ck-rail-empty">{t(locale, "rail.journal_empty")}</div>
          ) : (
            p.journal.slice(0, 6).map((j, i) => (
              <div key={i} className="ck-rail-journal">
                <span className="num ck-rail-jtime">{j.time}</span>
                <span className={`ck-rail-jtext ${j.tone}`}>{j.text}</span>
              </div>
            ))
          )}
        </>
      )}

      {past.length > 0 && (
        <>
          <button type="button" className="ck-rail-title ck-rail-archtoggle" onClick={() => setArchiveOpen((v) => !v)}>
            {t(locale, "rail.archive", { year: String(today.getFullYear()), n: past.length })} {archiveOpen ? "▾" : "▸"}
          </button>
          {archiveOpen &&
            past.map((g) => (
              <button key={g.gig_id} type="button" className={`ck-rail-row past${isCancelled(g) ? " cancelled" : ""}`} onClick={() => p.onOpen(g.gig_id)}>
                <span className="ck-rail-line">{railLine(g, locale)}</span>
                {isCancelled(g) && <span className="ck-rail-st">{statusChip(g)}</span>}
              </button>
            ))}
        </>
      )}
      {hasTrash && p.editor !== undefined && (
        <Suspense fallback={null}>
          <GigTrash locale={locale} all={p.all} onOpen={p.onOpen} editor={p.editor} />
        </Suspense>
      )}
    </aside>
  );
}
